import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

/**
 * SEND-194 — self-service password change for a signed-in user.
 *
 * The value is never logged, never stored in anything but the input's own
 * state, and never copied to the clipboard by this component. The reveal
 * toggles the input's own `type` and nothing else — the SEND-191 shape.
 */
export const ChangePasswordCard = () => {
  const t = useT();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lockout, setLockout] = useState("");

  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = current && next && confirm && !mismatch && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLockout("");
    if (next !== confirm) {
      setError(t("account.mismatch"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setCurrent("");
      setNext("");
      setConfirm("");
      setReveal(false);
      toast.success(t("account.changed"));
      if (data?.is_seeded_admin) toast.message(t("account.seededNote"), { duration: 12000 });
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (status === 429) setLockout(msg);
      else setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const field = (id, label, value, setValue, autoComplete) => (
    <label className="block mb-3">
      <span className="block text-xs uppercase tracking-[0.12em] text-[var(--muted)] mb-1">
        {label}
      </span>
      <div className="relative">
        <input
          className="input w-full pr-10"
          type={reveal ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          name={id}
          data-testid={id}
        />
      </div>
    </label>
  );

  return (
    <div className="card p-6 mb-6 max-w-2xl" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-4">
        <KeyRound className="w-5 h-5 text-[var(--brand-text)]" />
        <div className="section-tag">{t("account.yourPassword")}</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-5">{t("account.blurb")}</p>

      <form onSubmit={submit}>
        {field("current-password-input", t("account.current"), current, setCurrent, "current-password")}
        {field("new-password-input", t("account.new"), next, setNext, "new-password")}
        {field("confirm-password-input", t("account.confirm"), confirm, setConfirm, "new-password")}

        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          aria-pressed={reveal}
          aria-label={reveal ? t("auth.hidePassword") : t("auth.showPassword")}
          className="inline-flex items-center gap-2 text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors mb-4"
          data-testid="change-password-reveal-btn"
        >
          {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {reveal ? t("auth.hidePassword") : t("auth.showPassword")}
        </button>

        {mismatch && (
          <div className="text-sm text-[var(--danger,#b91c1c)] mb-3" data-testid="change-password-mismatch">
            {t("account.mismatch")}
          </div>
        )}
        {error && (
          <div
            className="text-sm border-l-2 border-[var(--danger,#b91c1c)] pl-3 py-1 mb-3 text-[var(--ink)]"
            data-testid="change-password-error"
          >
            {error}
          </div>
        )}
        {lockout && (
          <div
            className="text-sm border-l-2 border-[var(--danger,#b91c1c)] pl-3 py-2 mb-3 text-[var(--ink)]"
            data-testid="change-password-lockout"
          >
            <div className="font-semibold uppercase tracking-wide text-xs mb-1">
              {t("account.lockedOut")}
            </div>
            {lockout}
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            className="btn-primary"
            type="submit"
            disabled={!ready}
            data-testid="change-password-submit"
          >
            {busy ? t("common.saving") : t("account.changeBtn")}
          </button>
          <span className="text-xs text-[var(--muted)]" data-testid="change-password-sessions-note">
            {t("account.sessionsNote")}
          </span>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordCard;
